
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReadAloudBar from "../components/ReadAloudBar";
import { collectUiStringsForTts } from "../utils/collectUiStringsForTts";
import "./BooksLanding.css";
import authorPhoto from "../assets/author-photo.jpg";

const content = {
  he: {
    htmlLang: "he",
    dir: "rtl",
    brand: "לא שבור לנצח",
    navAria: "ניווט ראשי",
    navHome: "בית",
    navBook: "הספר",
    navInnerVoice: "InnerVoice",
    navOrasis: "Orasis",
    proofTags: ["אמיתי", "רגשי", "מרפא"],
    langLabel: "עברית",
    heroEyebrow: "סיפור אחד. שלוש שפות. לב אחד שמבקש להחלים.",
    title: "לא שבור לנצח",
    subtitle: "איך ילד שבור למד לאהוב, להחלים, ולהתחיל מחדש.",
    description:
      "זה לא עוד ספר שקוראים ושוכחים. זה מסע אמיתי, חשוף, וכואב — על אהבה, נטישה, שברון לב, והאפשרות לבחור בחיים מחדש.",
    primaryCta: "לקנייה בעברית",
    secondaryCta: "קראו משפטים מהספר",
    primaryHref: "https://a.co/d/00vNqflL",
    quoteTop: "לפעמים אתה לא מתגעגע לאדם — אתה מתגעגע למי שהיית לידו.",
    quoteTopSource: "מתוך הספר",
    painTitle: "אם נשברת מבפנים — הספר הזה בשבילך",
    painText:
      "אם חווית פרידה, גירושים, בדידות, פחד מנטישה או תחושת ריק שלא מרפה — הספר הזה נכתב מתוך המקומות האלה בדיוק.",
    cardsTitle: "למי הספר הזה מדבר",
    cards: [
      {
        title: "למי שעבר שברון לב",
        text: "אם איבדת מישהו — או חלק מעצמך — תמצא כאן מילים שיראו אותך באמת."
      },
      {
        title: "למי שמתחיל מחדש",
        text: "כשהחיים מתפרקים, משהו עמוק יותר יכול להיוולד. הספר הזה פוגש בדיוק את הרגע הזה."
      },
      {
        title: "למי שנשאר חזק יותר מדי זמן",
        text: "למי שתמיד תפקד, תמיד החזיק, תמיד שרד — אבל בפנים כבר התעייף."
      }
    ],
    quotesTitle: "משפטים שנשארים בלב",
    quotes: [
      "לא כל סוף הוא כישלון. לפעמים הוא רק ההתחלה של חיים חדשים.",
      "יש לילות שאף אחד לא רואה אותך נשבר — אבל הם הכי אמיתיים.",
      "אם הגוף שלך כבר הרגיש את זה, אל תיתן ללב שלך להכחיש."
    ],
    finalTitle: "יש ספרים שקוראים. ויש ספרים שמרגישים.",
    finalText:
      "״לא שבור לנצח״ הוא לא רק סיפור אישי. הוא מראה, מסע, ותזכורת לכך שגם לב שנשבר — יכול להתחיל מחדש.",
    finalCta: "אני רוצה את הספר",
    socialProofTitle: "למה קוראים מתחברים לספר הזה",
    socialProof: [
      "ספר שנכתב מתוך שבר אמיתי, בלי מסכות ובלי קלישאות.",
      "כתיבה שמצליחה לגעת במקומות שרבים לא מצליחים לומר בקול.",
      "שילוב נדיר של אמת, רגש, ופשטות שמרגישה קרובה ללב."
    ],
    availabilityTitle: "זמינות ופורמטים",
    availabilityItems: [
      "זמין לרכישה באמזון.",
      "עברית: מהדורה מודפסת.",
      "English: Paperback + Kindle.",
      "Español: Paperback + Kindle."
    ],
    languagesTitle: "בחרו את השפה שלכם",
    langCards: [
      {
        label: "עברית",
        title: "לא שבור לנצח",
        text: "הגרסה העברית המקורית, עמוקה, חמה וישירה ללב.",
        button: "לקנייה בעברית",
        href: "https://a.co/d/00vNqflL"
      },
      {
        label: "English",
        title: "Not Broken Forever",
        text: "The full emotional journey in English.",
        button: "Buy in English",
        href: "https://a.co/d/01SkjbYy"
      },
      {
        label: "Español",
        title: "No Roto Para Siempre",
        text: "La historia completa en español, con toda su emoción.",
        button: "Comprar en Español",
        href: "https://a.co/d/02pSYGwh"
      }
    ],
    faqTitle: "שאלות נפוצות",
    faq: [
      {
        q: "האם זה ספר אישי או ספר שעוזר לאחרים גם?",
        a: "זה ספר אישי מאוד, אבל קוראים רבים מרגישים שהוא גם מספר את הסיפור שלהם. הוא נקרא כמו סיפור אמיתי, ומרגיש כמו מראה."
      },
      {
        q: "מה ההבדל בין הספר לבין InnerVoice?",
        a: "הספר הוא מסע קריאה אישי ועמוק. InnerVoice הוא תהליך יומי מונחה של כתיבה ועבודה פנימית. אפשר להתחיל מכל אחד מהם."
      },
      {
        q: "האם צריך לעבור משבר כדי להתחבר אליו?",
        a: "לא. הספר מתאים לכל מי שמרגיש שהוא רוצה להבין את עצמו לעומק, לרפא שכבות ישנות, ולבנות התחלה חדשה."
      },
      {
        q: "באיזו שפה כדאי לי לקנות?",
        a: "בחר את השפה שבה אתה קורא מהלב בצורה הכי טבעית. המסר הרגשי נשמר בכל אחת מהגרסאות."
      },
      {
        q: "האם הספר מתאים לגברים וגם לנשים?",
        a: "כן. זהו סיפור אנושי שנוגע ברגש, באובדן, בהחלמה ובבחירה בחיים מחדש — מעבר למגדר."
      }
    ],
    authorTitle: "על המחבר",
    authorName: "Phineas J. Cohen",
    authorText:
      "Phineas J. Cohen כתב את 'לא שבור לנצח' מתוך מסע אישי של כאב, אהבה, ריפוי והתחלה מחדש. זהו ספר שנולד מתוך החיים עצמם — כדי לגעת בלבבות של אנשים שמחפשים אמת, נחמה ותקווה.",
    footer: "Phineas J. Cohen — Not Broken Forever",
    innervoiceTeaser: "מחפשים תהליך יומי מובנה באפליקציה?",
    innervoiceTeaserCta: "InnerVoice — המערכת",
    crossSellTitle: "עוד דרכים להבין את עצמך",
    crossSellBody: "הספר הוא מסע קריאה. InnerVoice הוא תהליך יומי מונחה. Orasis הוא דוח עומק לשאלה שמעסיקה אותך עכשיו.",
    crossSellIvCta: "להתחיל מסע עם InnerVoice",
    crossSellOrasisCta: "לקבל דוח Orasis",
    contactTitle: "רוצים לכתוב לי?",
    contactBody: "שאלות על הספר, ההזמנה או המסע האישי שלכם.",
    contactEmail: "support@notbrokenforever.com",
    contactWhatsApp: "https://wa.me/message/Y2NHI6KGHYJAN1",
    contactInstagram: "https://instagram.com/notbrokenforever",
    contactWhatsAppLabel: "WhatsApp",
    contactInstagramLabel: "Instagram",
  },

  en: {
    htmlLang: "en",
    dir: "ltr",
    brand: "Not Broken Forever",
    navAria: "Main navigation",
    navHome: "Home",
    navBook: "Book",
    navInnerVoice: "InnerVoice",
    navOrasis: "Orasis",
    proofTags: ["Honest", "Emotional", "Healing"],
    langLabel: "English",
    heroEyebrow: "One story. Three languages. One heart asking to heal.",
    title: "Not Broken Forever",
    subtitle: "How a broken boy learned to love, heal, and begin again.",
    description:
      "This is not just another book you read and forget. It is a real, exposed, deeply emotional journey through abandonment, heartbreak, loneliness, and the courage to start over.",
    primaryCta: "Buy in English",
    secondaryCta: "Read Quotes",
    primaryHref: "https://a.co/d/01SkjbYy",
    quoteTop: "Sometimes you don’t miss the person — you miss who you were beside them.",
    quoteTopSource: "From the book",
    painTitle: "If your heart was broken… this book is for you",
    painText:
      "If you’ve lived through heartbreak, divorce, loneliness, abandonment, or the silent exhaustion of always being the strong one — this book was written from exactly that place.",
    cardsTitle: "Who this book speaks to",
    cards: [
      {
        title: "For the brokenhearted",
        text: "If you lost someone — or lost a part of yourself — these pages will meet you there."
      },
      {
        title: "For those starting over",
        text: "When life falls apart, something deeper can begin. This book speaks to that turning point."
      },
      {
        title: "For those tired of surviving",
        text: "For the ones who kept functioning, kept carrying, kept smiling — while something inside quietly hurt."
      }
    ],
    quotesTitle: "Lines that stay with you",
    quotes: [
      "Not every ending is a failure. Sometimes it is the beginning of a new life.",
      "There are nights no one sees you breaking — but they are the most real.",
      "If your body already felt it, don’t let your heart deny it."
    ],
    finalTitle: "Some books are read. Some books are felt.",
    finalText:
      "Not Broken Forever is more than a memoir. It is a mirror, a healing journey, and a reminder that even a shattered heart can begin again.",
    finalCta: "Get the Book",
    socialProofTitle: "Why readers connect with this book",
    socialProof: [
      "Written from real heartbreak, with no masks and no cliches.",
      "Language that reaches places many people struggle to say out loud.",
      "A rare mix of emotional honesty and practical inner clarity."
    ],
    availabilityTitle: "Availability and formats",
    availabilityItems: [
      "Available on Amazon.",
      "Hebrew: Paperback.",
      "English: Paperback + Kindle.",
      "Spanish: Paperback + Kindle."
    ],
    languagesTitle: "Choose your language",
    langCards: [
      {
        label: "Hebrew",
        title: "לא שבור לנצח",
        text: "The original Hebrew version, direct, raw, and heartfelt.",
        button: "Buy in Hebrew",
        href: "https://a.co/d/00vNqflL"
      },
      {
        label: "English",
        title: "Not Broken Forever",
        text: "The full emotional journey in English.",
        button: "Buy in English",
        href: "https://a.co/d/01SkjbYy"
      },
      {
        label: "Español",
        title: "No Roto Para Siempre",
        text: "The complete story in Spanish, with all its emotional depth.",
        button: "Buy in Spanish",
        href: "https://a.co/d/02pSYGwh"
      }
    ],
    faqTitle: "Frequently Asked Questions",
    faq: [
      {
        q: "Is this memoir, or can it help other readers too?",
        a: "It is a deeply personal story, but many readers feel they are seeing themselves inside it. It reads like a true story and works like a mirror."
      },
      {
        q: "What is the difference between this book and InnerVoice?",
        a: "The book offers deep personal reading. InnerVoice offers a guided daily process of writing and inner work. You can start with either."
      },
      {
        q: "Do I need to be in a major crisis to connect with it?",
        a: "No. It is for anyone seeking emotional clarity, healing, and a meaningful inner reset."
      },
      {
        q: "Which language should I buy?",
        a: "Choose the language your heart reads in most naturally."
      },
      {
        q: "Is it relevant for both men and women?",
        a: "Yes. It speaks to universal themes of loss, healing, and rebuilding from within."
      }
    ],
    authorTitle: "About the Author",
    authorName: "Phineas J. Cohen",
    authorText:
      "Phineas J. Cohen wrote 'Not Broken Forever' from a deeply personal journey of pain, love, healing, and starting over. This book was born from real life — to reach the hearts of people searching for truth, comfort, and hope.",
    footer: "Phineas J. Cohen — Not Broken Forever",
    innervoiceTeaser: "Looking for a structured daily process in the app?",
    innervoiceTeaserCta: "Try InnerVoice",
    crossSellTitle: "More ways to understand yourself",
    crossSellBody: "The book is a reading journey. InnerVoice is a guided daily process. Orasis is an in-depth report for the question on your mind now.",
    crossSellIvCta: "Start a journey with InnerVoice",
    crossSellOrasisCta: "Get an Orasis report",
    contactTitle: "Want to reach out?",
    contactBody: "Questions about the book, your order, or your personal journey.",
    contactEmail: "support@notbrokenforever.com",
    contactWhatsApp: "https://wa.me/message/Y2NHI6KGHYJAN1",
    contactInstagram: "https://instagram.com/notbrokenforever",
    contactWhatsAppLabel: "WhatsApp",
    contactInstagramLabel: "Instagram",
  },

  es: {
    htmlLang: "es",
    dir: "ltr",
    brand: "No Roto Para Siempre",
    navAria: "Navegación principal",
    navHome: "Inicio",
    navBook: "Libro",
    navInnerVoice: "InnerVoice",
    navOrasis: "Orasis",
    proofTags: ["Honesto", "Emocional", "Sanador"],
    langLabel: "Español",
    heroEyebrow: "Una historia. Tres idiomas. Un corazón que busca sanar.",
    title: "No Roto Para Siempre",
    subtitle: "Cómo un niño roto aprendió a amar, sanar y empezar de nuevo.",
    description:
      "Este no es solo otro libro que se lee y se olvida. Es un viaje real, íntimo y profundo sobre abandono, dolor, soledad y el valor de volver a empezar.",
    primaryCta: "Comprar en Español",
    secondaryCta: "Leer Frases",
    primaryHref: "https://a.co/d/02pSYGwh",
    quoteTop: "A veces no extrañas a la persona, sino a quien eras a su lado.",
    quoteTopSource: "Del libro",
    painTitle: "Si te rompiste por dentro… este libro es para ti",
    painText:
      "Si has vivido una ruptura, divorcio, soledad, abandono o el cansancio silencioso de ser siempre el fuerte — este libro fue escrito exactamente desde ese lugar.",
    cardsTitle: "Para quién es este libro",
    cards: [
      {
        title: "Para corazones rotos",
        text: "Si perdiste a alguien — o una parte de ti — estas páginas te encontrarán allí."
      },
      {
        title: "Para quienes empiezan de nuevo",
        text: "Cuando la vida se rompe, algo más profundo puede comenzar. Este libro habla de ese punto de giro."
      },
      {
        title: "Para quienes se cansaron de sobrevivir",
        text: "Para quienes siguieron funcionando, cargando y sonriendo — mientras por dentro algo seguía doliendo."
      }
    ],
    quotesTitle: "Frases que se quedan contigo",
    quotes: [
      "No todo final es un fracaso. A veces es el comienzo de una nueva vida.",
      "Hay noches en que nadie te ve romperte, pero son las más reales.",
      "Si tu cuerpo ya lo sintió, no dejes que tu corazón lo niegue."
    ],
    finalTitle: "Hay libros que se leen. Y hay libros que se sienten.",
    finalText:
      "No Roto Para Siempre es más que una memoria. Es un espejo, un viaje de sanación y un recordatorio de que incluso un corazón roto puede volver a empezar.",
    finalCta: "Quiero el libro",
    socialProofTitle: "Por qué conecta con lectores",
    socialProof: [
      "Escrito desde una herida real, sin máscaras ni frases vacías.",
      "Una voz que pone palabras donde muchos no logran hablar.",
      "Combina verdad emocional con una sensación real de avance interno."
    ],
    availabilityTitle: "Disponibilidad y formatos",
    availabilityItems: [
      "Disponible en Amazon.",
      "Hebreo: edición impresa.",
      "English: Paperback + Kindle.",
      "Español: edición impresa + Kindle."
    ],
    languagesTitle: "Elige tu idioma",
    langCards: [
      {
        label: "Hebrew",
        title: "לא שבור לנצח",
        text: "La versión original en hebreo, directa y profundamente emocional.",
        button: "Comprar en Hebreo",
        href: "https://a.co/d/00vNqflL"
      },
      {
        label: "English",
        title: "Not Broken Forever",
        text: "La experiencia emocional completa en inglés.",
        button: "Buy in English",
        href: "https://a.co/d/01SkjbYy"
      },
      {
        label: "Español",
        title: "No Roto Para Siempre",
        text: "La historia completa en español, con toda su emoción.",
        button: "Comprar en Español",
        href: "https://a.co/d/02pSYGwh"
      }
    ],
    faqTitle: "Preguntas Frecuentes",
    faq: [
      {
        q: "¿Es memoria personal o también puede ayudar a otros?",
        a: "Es una historia profundamente personal, pero muchos lectores sienten que también habla de ellos. Se lee como una historia real y funciona como un espejo."
      },
      {
        q: "¿Cuál es la diferencia entre el libro e InnerVoice?",
        a: "El libro ofrece lectura emocional profunda. InnerVoice ofrece un proceso diario guiado de escritura y trabajo interno. Puedes empezar por cualquiera."
      },
      {
        q: "¿Necesito estar en una crisis grande para conectar con él?",
        a: "No. Es para quien quiera entenderse mejor, sanar capas emocionales y empezar una nueva etapa con más claridad."
      },
      {
        q: "¿Qué idioma me conviene comprar?",
        a: "Elige el idioma en el que tu corazón lee con más naturalidad."
      },
      {
        q: "¿Es un libro para hombres y mujeres?",
        a: "Sí. Habla de temas humanos universales: pérdida, sanación y reconstrucción interior."
      }
    ],
    authorTitle: "Sobre el autor",
    authorName: "Phineas J. Cohen",
    authorText:
      "Phineas J. Cohen escribió 'No Roto Para Siempre' desde un viaje profundamente personal de dolor, amor, sanación y nuevos comienzos. Este libro nació de la vida real — para tocar el corazón de personas que buscan verdad, consuelo y esperanza.",
    footer: "Phineas J. Cohen — Not Broken Forever",
    innervoiceTeaser: "¿Quieres un proceso guiado día a día en la app?",
    innervoiceTeaserCta: "Probar InnerVoice",
    crossSellTitle: "Más formas de entenderte",
    crossSellBody: "El libro es un viaje de lectura. InnerVoice es un proceso diario guiado. Orasis es un informe en profundidad para la pregunta que te importa ahora.",
    crossSellIvCta: "Empezar un viaje con InnerVoice",
    crossSellOrasisCta: "Recibir un informe Orasis",
    contactTitle: "¿Quieres escribirme?",
    contactBody: "Preguntas sobre el libro, tu pedido o tu viaje personal.",
    contactEmail: "support@notbrokenforever.com",
    contactWhatsApp: "https://wa.me/message/Y2NHI6KGHYJAN1",
    contactInstagram: "https://instagram.com/notbrokenforever",
    contactWhatsAppLabel: "WhatsApp",
    contactInstagramLabel: "Instagram",
  }
};

function withLang(path, lang) {
  const join = path.includes("?") ? "&" : "?";
  return `${path}${join}lang=${encodeURIComponent(lang)}`;
}

function detectInitialLang() {
  const qs = new URLSearchParams(window.location.search);
  const urlLang = qs.get("lang");
  if (urlLang && content[urlLang]) return urlLang;

  const saved = window.localStorage.getItem("books_landing_lang");
  if (saved && content[saved]) return saved;

  const browserLang = (navigator.language || "en").toLowerCase();
  if (browserLang.startsWith("he")) return "he";
  if (browserLang.startsWith("es")) return "es";
  return "en";
}

export default function BooksLanding() {
  const [lang, setLang] = useState(detectInitialLang);
  const t = useMemo(() => content[lang] || content.en, [lang]);
  const bookReadAloud = useMemo(() => collectUiStringsForTts(t), [t]);

  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.documentElement.dir = t.dir;
    window.localStorage.setItem("books_landing_lang", lang);
  }, [lang, t]);

  const switchLang = (nextLang) => {
    setLang(nextLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", nextLang);
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <div className={`books-page books-page--${lang}`} dir={t.dir}>
      <header className="books-topbar">
        <div className="books-shell books-topbar__inner">
          <Link className="books-brand books-brand--link" to={withLang("/", lang)}>
            {t.brand}
          </Link>

          <nav className="books-nav" aria-label={t.navAria}>
            <Link to={withLang("/", lang)}>{t.navHome}</Link>
            <Link to={withLang("/book", lang)}>{t.navBook}</Link>
            <Link to={withLang("/innervoice", lang)}>{t.navInnerVoice}</Link>
            <a href="/orasis">{t.navOrasis}</a>
          </nav>

          <div className="books-topbar__actions">
            <div className="books-lang-switch">
              <button
                className={lang === "he" ? "active" : ""}
                onClick={() => switchLang("he")}
                type="button"
              >
                עברית
              </button>
              <button
                className={lang === "en" ? "active" : ""}
                onClick={() => switchLang("en")}
                type="button"
              >
                English
              </button>
              <button
                className={lang === "es" ? "active" : ""}
                onClick={() => switchLang("es")}
                type="button"
              >
                Español
              </button>
            </div>

          </div>
        </div>
      </header>

      <main>
        <section className="books-hero">
          <div className="books-shell books-hero__grid">
            <div className="books-hero__content">
              <div className="books-pill">{t.heroEyebrow}</div>
              <h1>{t.title}</h1>
              <p className="books-hero__subtitle">{t.subtitle}</p>
              <p className="books-hero__description">{t.description}</p>

              <div className="books-hero__actions">
                <a
                  className="books-btn books-btn--primary"
                  href={t.primaryHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.primaryCta}
                </a>
                <a className="books-btn books-btn--secondary" href="#quotes">
                  {t.secondaryCta}
                </a>
              </div>

              <div className="books-proof-row">
                {t.proofTags.map((tag) => (
                  <span key={tag}>✓ {tag}</span>
                ))}
              </div>

              <p className="books-innervoice-teaser">
                {t.innervoiceTeaser}{" "}
                <Link className="books-innervoice-teaser__link" to={`/innervoice?lang=${encodeURIComponent(lang)}`}>
                  {t.innervoiceTeaserCta}
                </Link>
              </p>
              <ReadAloudBar text={bookReadAloud} lang={lang} />
            </div>

            <div className="books-hero__visual">
              <div className="books-quote-box">
                <div className="books-quote-mark">“</div>
                <div className="books-quote-text">{t.quoteTop}</div>
                <div className="books-quote-source">{t.quoteTopSource}</div>
              </div>

              <div className="books-cover-stack">
                <img
                  src="/books/book-es.png"
                  alt="No Roto Para Siempre"
                  className="books-cover-img books-cover-img--left"
                />
                <img
                  src="/books/book-en.png"
                  alt="Not Broken Forever"
                  className="books-cover-img books-cover-img--center"
                />
                <img
                  src="/books/book-he.png"
                  alt="לא שבור לנצח"
                  className="books-cover-img books-cover-img--right"
                />
              </div>

              <div className="books-visual-cta">
                <strong>{t.painTitle}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="books-dark-band">
          <div className="books-shell books-dark-band__inner">
            <div className="books-video-card books-trust-card">
              <div className="books-video-placeholder books-trust-placeholder">
                <h3 className="books-trust-title">{t.socialProofTitle}</h3>
                <ul className="books-trust-list">
                  {t.socialProof.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="books-dark-copy">
              <h2>{t.painTitle}</h2>
              <p>{t.painText}</p>
            </div>
          </div>
        </section>
        <section className="books-section books-section--availability">
          <div className="books-shell">
            <div className="books-availability-card">
              <h2>{t.availabilityTitle}</h2>
              <ul>
                {t.availabilityItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>


        <section className="books-section books-section--light">
          <div className="books-shell">
            <div className="books-section__head">
              <h2>{t.cardsTitle}</h2>
            </div>
            <div className="books-cards-grid">
              {t.cards.map((card, index) => (
                <article key={index} className="books-card">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="quotes" className="books-section books-section--quotes">
          <div className="books-shell">
            <div className="books-section__head">
              <h2>{t.quotesTitle}</h2>
            </div>
            <div className="books-quotes-grid">
              {t.quotes.map((quote, index) => (
                <article key={index} className="books-quote-card">
                  <p>{quote}</p>
                  <span>{t.quoteTopSource}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="books-section books-section--languages">
          <div className="books-shell">
            <div className="books-section__head">
              <h2>{t.languagesTitle}</h2>
            </div>
            <div className="books-language-grid">
              {t.langCards.map((card, index) => (
                <article key={index} className="books-language-card">
                  <div className="books-language-label">{card.label}</div>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                  <a
                    className="books-btn books-btn--primary"
                    href={card.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {card.button}
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="books-section books-section--cross-sell">
          <div className="books-shell">
            <div className="books-cross-sell">
              <h2>{t.crossSellTitle}</h2>
              <p>{t.crossSellBody}</p>
              <div className="books-cross-sell__actions">
                <Link className="books-btn books-btn--secondary" to={withLang("/innervoice", lang)}>
                  {t.crossSellIvCta}
                </Link>
                <a className="books-btn books-btn--ghost" href="/orasis">
                  {t.crossSellOrasisCta}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="books-section books-section--faq">
          <div className="books-shell">
            <div className="books-section__head">
              <h2>{t.faqTitle}</h2>
            </div>
            <div className="books-faq-list">
              {t.faq.map((item, index) => (
                <details key={index} className="books-faq-item">
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="books-section books-section--author">
          <div className="books-shell">
            <div className="books-author-card">
              <div className="books-author-photo-wrap">
                <img
                  src={authorPhoto}
                  alt={t.authorName}
                  className="books-author-photo"
                />
              </div>

              <div className="books-author-content">
                <div className="books-section__head books-section__head--left">
                  <h2>{t.authorTitle}</h2>
                </div>
                <h3 className="books-author-name">{t.authorName}</h3>
                <p className="books-author-text">{t.authorText}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="books-section books-section--cta">
          <div className="books-shell">
            <div className="books-big-cta">
              <div className="books-pill books-pill--light">{t.langLabel}</div>
              <h2>{t.finalTitle}</h2>
              <p>{t.finalText}</p>
              <a
                className="books-btn books-btn--primary books-btn--large"
                href={t.primaryHref}
                target="_blank"
                rel="noreferrer"
              >
                {t.finalCta}
              </a>
            </div>
          </div>
        </section>

        <section className="books-section books-section--contact">
          <div className="books-shell">
            <div className="books-contact-card">
              <h2>{t.contactTitle}</h2>
              <p>{t.contactBody}</p>
              <div className="books-contact-links">
                <a href={`mailto:${t.contactEmail}`}>{t.contactEmail}</a>
                <a href={t.contactWhatsApp} target="_blank" rel="noreferrer">
                  {t.contactWhatsAppLabel}
                </a>
                <a href={t.contactInstagram} target="_blank" rel="noreferrer">
                  {t.contactInstagramLabel}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <a
        className="books-sticky-buy"
        href={t.primaryHref}
        target="_blank"
        rel="noreferrer"
      >
        {t.finalCta}
      </a>

      <footer className="books-footer">
        <div className="books-shell">
          <p>{t.footer}</p>
        </div>
      </footer>
    </div>
  );
}
